import { computed, inject, Service } from '@angular/core';
import { SessionService } from './session.service';

/**
 * Whether the signed-in customer may trade کالا — that is, holds a کد بورس کالا.
 *
 * The gate is `imeRegistered` on `GET /users/profile`. A customer without the code signs
 * in and browses the public pages like anyone else; what they cannot do is submit an order
 * or move money, and this is where those three entry points read that from — the market
 * notice, the order form's ارسال سفارش and the wallet's واریز / برداشت.
 *
 * The reads are gated too — the commodity endpoints answer them with the same refusal —
 * and a refused read is not a failure to report either: پرتفوی, سفارشات and the rest each
 * have an empty state for an account with nothing on it, and that is what a refusal means.
 * So those screens' stores read {@link denied} on a failed load and land empty rather than
 * erroring, while a load that fails for any other reason is still the error it looks like.
 *
 * The profile is the only source and it lands a moment after the session opens, so
 * {@link denied} reads `false` until it does. That is deliberate on both sides: a page must
 * not accuse anyone of anything before it has been told, and a click that somehow beats the
 * profile goes through to the backend — which refuses it, and a refusal is a far better
 * outcome than blocking a customer whose profile simply failed to load.
 */
@Service()
export class CommodityAccessService {
	private readonly session = inject(SessionService);

	/** The customer has no کد بورس کالا — known so, not assumed. */
	readonly denied = computed(() => {
		const profile = this.session.profile();

		return profile !== null && profile.imeRegistered !== true;
	});
}
